/**
 * Liste paginée par le serveur, pour les écrans de liste (mixin Vue).
 *
 *   mixins: [listeServeur('patients', { tri: 'pat.createdAt', sens: 'desc' })]
 *
 * Fournit : lignes, total, page, parPage, recherche, tri, sens, chargement,
 * erreur, et la méthode charger(). La recherche part après une courte pause
 * de frappe et revient à la page 1 ; changer de page, de taille ou de tri
 * recharge. L'écran peut définir :
 *   - filtresListe() : filtres propres ({ statut: 'X', du: '2026-01-01' })
 *     — à recharger avec rechargerListe() quand ils changent ;
 *   - apresChargement(lignes, reponse) : retouche des lignes reçues.
 * Gabarit : <Pagination v-model:page="page" v-model:par-page="parPage" :total="total" />
 */
import api, { messageErreur } from '@/api'

export function listeServeur(ressource, { tri = '', sens = 'desc', parPage = 25 } = {}) {
  return {
    data() {
      return {
        lignes: [],
        total: 0,
        page: 1,
        parPage,
        recherche: '',
        tri,
        sens,
        chargement: false,
        erreur: '',
        _minuterieRecherche: null,
        _numeroRequete: 0,
      }
    },
    watch: {
      recherche() {
        clearTimeout(this._minuterieRecherche)
        this._minuterieRecherche = setTimeout(() => this.rechargerListe(), 350)
      },
      page() { this.charger() },
      parPage() { this.rechargerListe() },
      tri() { this.rechargerListe() },
      sens() { this.rechargerListe() },
    },
    created() {
      this.charger()
    },
    beforeUnmount() {
      clearTimeout(this._minuterieRecherche)
    },
    methods: {
      /** Revient à la page 1 et recharge (après un changement de filtre). */
      rechargerListe() {
        if (this.page !== 1) this.page = 1 // le watcher de page recharge
        else this.charger()
      },
      async charger() {
        const numero = ++this._numeroRequete
        this.chargement = true
        this.erreur = ''
        try {
          const filtres = typeof this.filtresListe === 'function' ? this.filtresListe() : {}
          const res = await api.liste(typeof ressource === 'function' ? ressource.call(this) : ressource, {
            page: this.page,
            itemsperpage: this.parPage,
            search: this.recherche.trim(),
            orderBy: this.tri,
            sortOrder: this.sens,
            ...filtres,
          })
          if (numero !== this._numeroRequete) return // réponse dépassée par une requête plus récente
          const corps = res.data || {}
          let lignes = Array.isArray(corps) ? corps : (corps.data || corps['hydra:member'] || [])
          if (typeof this.apresChargement === 'function') lignes = this.apresChargement(lignes, corps) || lignes
          this.lignes = lignes
          this.total = Number(corps.meta?.total_records ?? corps['hydra:totalItems'] ?? lignes.length)
          // Page devenue vide (suppression de la dernière ligne) : page précédente
          if (!lignes.length && this.page > 1 && this.total > 0) this.page = Math.ceil(this.total / this.parPage)
        } catch (e) {
          if (numero !== this._numeroRequete) return
          this.erreur = messageErreur(e, 'Impossible de charger la liste.')
          this.lignes = []
          this.total = 0
        } finally {
          if (numero === this._numeroRequete) this.chargement = false
        }
      },
      /** Numéro de ligne affiché (continue d'une page à l'autre). */
      rang(index) {
        return (this.page - 1) * this.parPage + index + 1
      },
      /** Clic sur un en-tête de colonne triable. */
      trierPar(colonne) {
        if (this.tri === colonne) this.sens = this.sens === 'asc' ? 'desc' : 'asc'
        else { this.tri = colonne; this.sens = 'asc' }
      },
    },
  }
}
